export function buildCategoryTree(categories = []) {
  const map = {};
  categories.forEach((c) => {
    map[c._id] = {
      ...c,
      id: c._id,
      children: [],
    };
  });

  const roots = [];
  categories.forEach((c) => {
    const node = map[c._id];
    const parentId = c.parent?._id || c.parent || null;
    if (parentId && map[parentId]) {
      map[parentId].children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortFn = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0);
  const sortTree = (nodes) => {
    nodes.sort(sortFn);
    nodes.forEach((n) => sortTree(n.children || []));
  };
  sortTree(roots);
  return roots;
}

/** Flat options for selects: { id, slug, name, label, shortLabel, path, depth, parentId } */
export function flattenCategoryOptions(
  categories = [],
  rows = [],
  ancestorNames = []
) {
  const tree =
    Array.isArray(categories) && categories[0]?.children !== undefined
      ? categories
      : buildCategoryTree(categories);

  tree.forEach((node) => {
    const name = node.name;
    const path = [...ancestorNames, name];
    const depth = Math.max(0, path.length - 1);
    const label = path.join(" › ");
    const shortLabel =
      path.length > 2 ? path.slice(-2).join(" › ") : label;

    rows.push({
      id: node._id || node.id,
      slug: node.slug,
      name,
      label,
      shortLabel,
      path,
      depth,
      parentId: node.parent?._id || node.parent || null,
      isActive: node.isActive !== false,
      sortOrder: node.sortOrder || 0,
      children: node.children || [],
    });

    if (node.children?.length) {
      flattenCategoryOptions(node.children, rows, path);
    }
  });

  return rows;
}

/** Top-level categories only, without their nested children */
export function getRootCategoryOptions(categories = []) {
  return flattenCategoryOptions(categories).filter((o) => o.depth === 0);
}

/** Prevent selecting a category as its own descendant parent */
export function getDescendantIds(categoryId, categories = []) {
  const tree = buildCategoryTree(categories);
  const ids = [];

  const walk = (nodes) => {
    for (const node of nodes) {
      if (String(node._id || node.id) === String(categoryId)) {
        const collect = (n) => {
          (n.children || []).forEach((child) => {
            ids.push(String(child._id || child.id));
            collect(child);
          });
        };
        collect(node);
        return true;
      }
      if (walk(node.children || [])) return true;
    }
    return false;
  };

  walk(tree);
  return ids;
}

/** Flat options for the subtree under a category (children + deeper), excluding the root itself */
export function getSubtreeOptions(categoryId, categories = []) {
  if (!categoryId) return [];
  const tree = buildCategoryTree(categories);

  const findNode = (nodes) => {
    for (const n of nodes) {
      if (String(n._id || n.id) === String(categoryId)) return n;
      const found = findNode(n.children || []);
      if (found) return found;
    }
    return null;
  };
  const root = findNode(tree);
  if (!root?.children?.length) return [];
  return flattenCategoryOptions(root.children);
}

/** Primary category on a product list/detail payload */
export function getProductCategory(product) {
  if (!product) return null;
  const primary = product.category;
  if (primary && typeof primary === "object" && primary.name) {
    return primary;
  }
  const extra = product.categories?.find?.(
    (c) => c && typeof c === "object" && c.name
  );
  if (extra) return extra;
  if (typeof primary === "string" && primary) {
    return { name: primary, slug: "" };
  }
  return null;
}

